import { type ReactNode } from "react";

import { BTText } from "@btnet/core-components/BTText";

import { BTLogo } from "commonComponents/btWrappers/BTLogo/BTLogo";
import { BTSpin } from "commonComponents/btWrappers/BTSpin/BTSpin";

export interface IBTAdminAuthScreenProps {
    title: string;
    description?: ReactNode;
    footer?: ReactNode;
    /** @default false */
    isBusy?: boolean;
}

/**
 * The chrome shared by every standalone BTAdmin auth screen (signed out, signing in): the
 * Buildertrend mark, a title, an optional description, an optional busy spinner, and an
 * optional action row, on a centered card.
 */
export function BTAdminAuthScreen({
    title,
    description,
    footer,
    isBusy = false,
}: IBTAdminAuthScreenProps) {
    return (
        <main className="bui-theme flex min-h-screen items-center justify-center bg-blue-90 p-4">
            <section
                className="w-96 max-w-full rounded-lg bg-white p-6 shadow-floating sm:p-8"
                data-testid="btAdminAuthScreenCard"
            >
                <BTLogo logoName="btLogoHorizontal" className="mb-8" width={160} />

                <BTText size="heavy-sm" tag="h1" testid="btAdminAuthScreenTitle">
                    {title}
                </BTText>

                {description !== undefined && (
                    <BTText
                        size="normal-md"
                        tag="p"
                        className="mt-1 text-fg-secondary"
                        testid="btAdminAuthScreenDescription"
                    >
                        {description}
                    </BTText>
                )}

                {isBusy && (
                    <div
                        className="mt-6 flex justify-center"
                        data-testid="btAdminAuthScreenSpinner"
                    >
                        <BTSpin size="large" />
                    </div>
                )}

                {footer !== undefined && (
                    <div className="mt-6 flex flex-col items-center gap-3">{footer}</div>
                )}
            </section>
        </main>
    );
}
